import { Package } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { promotedListingKeys } from '@/client/api/promotion-management/promoted-listing.query';
import {
  usePausePromotionMutation,
  useResumePromotionMutation,
} from '@/client/api/promotion-management/promotion.mutation';
import type { ListingPackage } from '@/client/types/promotion';
import { StatusBadge, ProgressBar, numberFmt } from './atoms';
import { fmtDate } from './listingMetrics';

/** Pause or resume one campaign. Expired and cancelled ones have nothing to act on. */
const PackageAction: React.FC<{ pkg: ListingPackage }> = ({ pkg }) => {
  const queryClient = useQueryClient();
  const pause = usePausePromotionMutation();
  const resume = useResumePromotionMutation();
  const isPaused = pkg.status === 'paused';
  if (!isPaused && pkg.status !== 'active') return null;

  const run = async () => {
    try {
      if (isPaused) {
        await resume.mutateAsync({ promid: pkg.promid });
      } else {
        const reason = window.prompt(`Why is "${pkg.name}" being paused? Kept in the audit trail.`);
        if (reason === null) return;
        await pause.mutateAsync({ promid: pkg.promid, reason: reason.trim() || undefined });
      }
      toast.success(isPaused ? 'Campaign resumed' : 'Campaign paused');
      queryClient.invalidateQueries({ queryKey: promotedListingKeys.all });
    } catch (err: any) {
      toast.error(err?.message || 'Could not update the campaign');
    }
  };

  return (
    <Button size="sm" variant="outline" disabled={pause.isPending || resume.isPending} onClick={run}>
      {isPaused ? 'Resume' : 'Pause'}
    </Button>
  );
};

export const PackagesPanel: React.FC<{ packages: ListingPackage[]; isLoading?: boolean }> = ({
  packages,
  isLoading,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
      <Package className="h-4 w-4 text-gray-500" />
      <h3 className="text-sm font-semibold text-gray-900">Promotional packages</h3>
      {!isLoading && <span className="text-xs text-gray-400">{packages.length}</span>}
    </div>

    {isLoading ? (
      <div className="divide-y divide-gray-100">
        {[0, 1].map((i) => (
          <div key={i} className="animate-pulse space-y-2 px-5 py-4">
            <div className="h-3 w-64 rounded bg-gray-200" />
            <div className="h-2.5 w-48 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    ) : packages.length === 0 ? (
      <p className="py-8 text-center text-sm text-gray-400">No promotion packages for this listing.</p>
    ) : (
      <div className="divide-y divide-gray-100">
        {packages.map((p) => (
          <div key={p.promid} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{p.name}</span>
                <StatusBadge status={p.status} />
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">{p.plan_name}</span>
                {' · '}
                {p.currency} {numberFmt.format(p.price)}
                {' · '}
                {fmtDate(p.date_start)} → {fmtDate(p.date_expired)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400">Impressions</p>
                <p className="text-sm text-gray-800">
                  {numberFmt.format(p.impressions)}{' '}
                  <span className="text-gray-400">/ {numberFmt.format(p.impression_target)}</span>
                </p>
                <div className="mt-1">
                  <ProgressBar value={p.impressions} target={p.impression_target} />
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400">Views</p>
                <p className="text-sm text-gray-800">{numberFmt.format(p.views)}</p>
                <p className="mt-1 text-xs text-gray-400">target rate {p.view_rate_target}%</p>
              </div>
              <PackageAction pkg={p} />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
